/**
 * FPD Insights Dashboard — v2 Feb 2026
 * 
 * Story-driven dashboard for DigniFi:
 * 1. The FPD landscape — honest view of the data
 * 2. What drives FPD — risk analysis by tier, dealer
 * 3. How early contact reduces FPD — measurable impact
 * 4. Active reduction efforts — what we're doing now
 * 
 * Dark theme consistent with portal design.
 */

import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, TrendingDown, TrendingUp, Shield, Phone, MessageSquare,
  CreditCard, Building, Loader2, ArrowDown, ArrowUp, Target, Zap, ArrowUpRight
} from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, ComposedChart, Line, Area, ReferenceLine,
} from "recharts";

const COLORS = {
  primary: "#3b82f6",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  muted: "#6b7280",
  purple: "#8b5cf6",
  cyan: "#06b6d4",
  emerald: "#10b981",
  rose: "#f43f5e",
  amber: "#f59e0b",
  slate: "#64748b",
};

const PIE_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6"];
const STATUS_COLORS: Record<string, string> = {
  "Collected": "#22c55e",
  "Pending": "#3b82f6",
  "Potential Default": "#f59e0b",
  "First Payment Default": "#ef4444",
};

function formatCurrency(val: number): string {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function formatPct(val: number): string {
  return `${val.toFixed(1)}%`;
}

function formatMonth(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  } catch {
    return String(dateStr);
  }
}

// Custom tooltip for dark theme
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' && (entry.name?.includes('Rate') || entry.name?.includes('%'))
            ? `${entry.value.toFixed(1)}%` 
            : Number(entry.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
}

// KPI Card — dark theme
function KPICard({ title, value, subtitle, icon: Icon, color, trend }: {
  title: string; value: string; subtitle?: string;
  icon: any; color: string; trend?: { direction: "up" | "down"; label: string };
}) {
  const colorMap: Record<string, { border: string; bg: string; icon: string }> = {
    [COLORS.primary]: { border: 'border-blue-500/30', bg: 'from-blue-500/5', icon: 'text-blue-400' },
    [COLORS.danger]: { border: 'border-red-500/30', bg: 'from-red-500/5', icon: 'text-red-400' },
    [COLORS.warning]: { border: 'border-amber-500/30', bg: 'from-amber-500/5', icon: 'text-amber-400' },
    [COLORS.success]: { border: 'border-green-500/30', bg: 'from-green-500/5', icon: 'text-green-400' },
    [COLORS.purple]: { border: 'border-purple-500/30', bg: 'from-purple-500/5', icon: 'text-purple-400' },
    [COLORS.cyan]: { border: 'border-cyan-500/30', bg: 'from-cyan-500/5', icon: 'text-cyan-400' },
  };
  const c = colorMap[color] || { border: 'border-blue-500/30', bg: 'from-blue-500/5', icon: 'text-blue-400' };

  return (
    <Card className={`${c.border} bg-gradient-to-br ${c.bg} to-transparent`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && (
              <div className="flex items-center gap-1 mt-1">
                {trend.direction === "down" ? (
                  <Badge variant="outline" className="text-xs text-green-400 border-green-500/30">
                    <ArrowDown className="h-3 w-3 mr-0.5" />
                    {trend.label}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-red-400 border-red-500/30">
                    <ArrowUp className="h-3 w-3 mr-0.5" />
                    {trend.label}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg bg-opacity-10`} style={{ backgroundColor: `${color}15` }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Impact comparison bar — dark theme
function ImpactBar({ label, rate, baseline, color }: {
  label: string; rate: number; baseline: number; color: string;
}) {
  const reduction = baseline > 0 ? ((baseline - rate) / baseline * 100) : 0;
  const isPositive = rate < baseline;
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-36 text-sm font-medium text-muted-foreground shrink-0">{label}</div>
      <div className="flex-1">
        <div className="h-8 bg-muted/20 rounded-full relative overflow-hidden">
          <div
            className="h-full rounded-full flex items-center justify-end pr-3 transition-all duration-1000"
            style={{ width: `${Math.min(rate / 20 * 100, 100)}%`, backgroundColor: color }}
          >
            <span className="text-xs font-bold text-white">{formatPct(rate)}</span>
          </div>
        </div>
      </div>
      <div className="w-24 text-right shrink-0">
        {isPositive ? (
          <Badge variant="outline" className="text-xs text-green-400 border-green-500/30">
            ↓ {reduction.toFixed(0)}% lower
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-red-400 border-red-500/30">
            ↑ {Math.abs(reduction).toFixed(0)}% higher
          </Badge>
        )}
      </div>
    </div>
  );
}

export default function FPDAnalysis() {
  const { organizationName, isLoading: orgLoading } = useOrganizationContext();

  // Story Section 1: The Problem
  const { data: fpdSummary } = trpc.dignifi.fpdSummary.useQuery();
  const { data: fpdByTier } = trpc.dignifi.fpdByTierDetailed.useQuery();
  const { data: firstPaymentStatus } = trpc.dignifi.firstPaymentStatus.useQuery();

  // Story Section 2: What Drives It
  const { data: worstDealers } = trpc.dignifi.worstFPDDealers.useQuery();
  const { data: bestDealers } = trpc.dignifi.bestFPDDealers.useQuery();

  // Story Section 3: How Contact Reduces FPD
  const { data: fpdMonthlyTrend } = trpc.dignifi.fpdMonthlyTrend.useQuery();
  const { data: autopayImpact } = trpc.dignifi.autopayFPDImpact.useQuery();
  const { data: bankImpact } = trpc.dignifi.bankAccountFPDImpact.useQuery();
  const { data: smsImpact } = trpc.dignifi.smsFPDImpact.useQuery();

  // Story Section 4: Active efforts — use enhancedSummary for accurate data
  const { data: enhancedSummary } = trpc.dignifi.enhancedSummary.useQuery();
  const { data: smsCampaign } = trpc.dignifi.smsCampaignStats.useQuery();

  if (orgLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // fpdSummary returns an OBJECT (not array) from getFPDSummary()
  const totalAccounts = Number((fpdSummary as any)?.total_accounts ?? 0);
  const totalFPD = Number((fpdSummary as any)?.fpd_count ?? 0);
  const overallFPDRate = totalAccounts > 0 ? (totalFPD / totalAccounts * 100) : 0;
  const totalPrincipal = Number((fpdSummary as any)?.total_principal ?? 0);
  const fpdPrincipal = Number((fpdSummary as any)?.fpd_principal ?? 0);

  // Feb 2026 rate for trend comparison
  const feb2026 = fpdMonthlyTrend?.find((m: any) => m.month?.includes("2026-02"));
  const latestFPDRate = feb2026 ? Number(feb2026.fpd_rate) : null;

  // Baseline FPD rate (pre-Welcome Agent average: Jan-Aug 2025)
  const preWelcomeMonths = fpdMonthlyTrend?.filter((m: any) => m.period === "Before Welcome Agent") ?? [];
  const baselineFPDRate = preWelcomeMonths.length > 0
    ? preWelcomeMonths.reduce((sum: number, m: any) => sum + Number(m.fpd_rate), 0) / preWelcomeMonths.length
    : 14.5;

  // Autopay/Bank/SMS baselines
  const noAutopayRate = Number(autopayImpact?.find((r: any) => r.status === "No Autopay")?.fpd_rate ?? 10.38);
  const noBankRate = Number(bankImpact?.find((r: any) => r.status === "No Bank Account")?.fpd_rate ?? 10.42);
  const noSmsRate = Number(smsImpact?.find((r: any) => r.status === "No SMS")?.fpd_rate ?? 10.52);

  // Chart data for monthly trend
  const trendData = fpdMonthlyTrend?.map((m: any) => ({
    month: formatMonth(m.month),
    fpd_rate: Number(m.fpd_rate),
    total_accounts: Number(m.total_accounts),
    fpd_count: Number(m.fpd_count),
    period: m.period,
    fpd_principal: Number(m.fpd_principal),
  })) ?? [];

  // Tier data for chart
  const tierData = fpdByTier?.map((t: any) => ({
    tier: t.tier,
    fpd_rate: Number(t.fpd_rate),
    total_accounts: Number(t.total_accounts),
    fpd_count: Number(t.fpd_count),
    fpd_principal: Number(t.fpd_principal),
    avg_loan: Number(t.avg_loan_size),
  })) ?? [];

  // First payment status pie data
  const statusData = firstPaymentStatus?.map((s: any) => ({
    name: s.status,
    value: Number(s.total_accounts),
    principal: Number(s.total_principal),
    avg_dpd: Number(s.avg_days_past_due),
    avg_payments: Number(s.avg_payments_made),
    collection_pct: Number(s.avg_collection_pct),
  })) ?? [];

  // Section 4 data from enhancedSummary
  const accountsContacted = Number((enhancedSummary as any)?.unique_consumers_contacted ?? 0);
  const smsSent = Number((smsCampaign as any)?.sms_sent ?? 0) + Number((smsCampaign as any)?.sms_delivered ?? 0);
  const autopayEnrolled = Number((enhancedSummary as any)?.autopay_enrolled ?? 0);
  const bankLinked = Number((enhancedSummary as any)?.bank_accounts_linked ?? 0);

  return (
    <DashboardLayout>
      <div className="space-y-8 p-6 pb-8">

        {/* ========== HEADER ========== */}
        <div className="bg-gradient-to-r from-blue-600/20 to-indigo-700/20 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-7 w-7 text-blue-400" />
            <h1 className="text-2xl font-bold">FPD Reduction Insights</h1>
          </div>
          <p className="text-muted-foreground text-sm max-w-3xl">
            Comprehensive analysis of First Payment Default patterns, risk drivers, and the measurable impact
            of early borrower engagement. This dashboard demonstrates how proactive outreach through the
            Welcome Agent program directly reduces FPD rates and protects portfolio value.
          </p>
        </div>

        {/* ========== SECTION 1: THE PROBLEM ========== */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-1 bg-red-500 rounded-full" />
            <h2 className="text-lg font-bold">The FPD Landscape</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPICard
              title="Total Accounts Analyzed"
              value={totalAccounts.toLocaleString()}
              subtitle="Jan 2025 – Feb 2026"
              icon={Target}
              color={COLORS.primary}
            />
            <KPICard
              title="Overall FPD Rate"
              value={formatPct(overallFPDRate)}
              subtitle={`${totalFPD.toLocaleString()} defaults`}
              icon={AlertTriangle}
              color={COLORS.danger}
            />
            <KPICard
              title="At-Risk Principal"
              value={formatCurrency(fpdPrincipal)}
              subtitle={`of ${formatCurrency(totalPrincipal)} total`}
              icon={TrendingDown}
              color={COLORS.warning}
            />
            {latestFPDRate !== null && (
              <KPICard
                title="Feb 2026 FPD Rate"
                value={formatPct(latestFPDRate)}
                subtitle="With active outreach"
                icon={TrendingDown}
                color={COLORS.success}
                trend={{
                  direction: "down",
                  label: `${((baselineFPDRate - latestFPDRate) / baselineFPDRate * 100).toFixed(0)}% vs pre-outreach avg`
                }}
              />
            )}
          </div>

          {/* Feb 2026 Payment Status Breakdown */}
          {statusData.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Current Payment Status — Feb 2026 Cohort</CardTitle>
                <CardDescription>
                  Breakdown of {statusData.reduce((s, d) => s + d.value, 0).toLocaleString()} accounts
                  by first payment loan status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {statusData.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(val: any) => Number(val).toLocaleString()}
                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3">
                    {statusData.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/10 border border-border/50">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.name] || PIE_COLORS[i] }} />
                          <div>
                            <p className="text-sm font-semibold">{s.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {s.value.toLocaleString()} accounts · {formatCurrency(s.principal)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{s.avg_payments.toFixed(1)} avg payments</p>
                          <p className="text-xs text-muted-foreground">{s.avg_dpd.toFixed(0)} avg DPD</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ========== SECTION 2: WHAT DRIVES FPD ========== */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-1 bg-amber-500 rounded-full" />
            <h2 className="text-lg font-bold">What Drives FPD</h2>
          </div>

          {/* FPD by Tier */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">FPD Rate by Consumer Tier</CardTitle>
              <CardDescription>
                Higher-risk tiers show significantly elevated default rates — Tier 2 defaults at nearly 3x the rate of Tier 7
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={tierData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="tier" tick={{ fontSize: 12 }} stroke="#666" />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#666" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#666" tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar yAxisId="left" dataKey="total_accounts" name="Total Accounts" fill={COLORS.primary} opacity={0.3} radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="fpd_count" name="FPD Count" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="fpd_rate" name="FPD Rate" stroke={COLORS.danger} strokeWidth={3} dot={{ fill: COLORS.danger, r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Dealer Performance - Top 5 Best and Worst */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  <CardTitle className="text-base">Highest FPD Dealers</CardTitle>
                </div>
                <CardDescription>Top 5 dealers with highest default rates (min 10 accounts)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(worstDealers ?? []).map((d: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{d.dealership}</p>
                          <p className="text-xs text-muted-foreground">
                            {Number(d.total_accounts).toLocaleString()} accounts · {formatCurrency(Number(d.total_principal))}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-400">{Number(d.fpd_rate).toFixed(1)}%</p>
                        <p className="text-xs text-muted-foreground">{Number(d.fpd_count)} defaults</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <CardTitle className="text-base">Best Performing Dealers</CardTitle>
                </div>
                <CardDescription>Top 5 dealers with lowest default rates (min 10 accounts)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(bestDealers ?? []).map((d: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{d.dealership}</p>
                          <p className="text-xs text-muted-foreground">
                            {Number(d.total_accounts).toLocaleString()} accounts · {formatCurrency(Number(d.total_principal))}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-400">{Number(d.fpd_rate).toFixed(1)}%</p>
                        <p className="text-xs text-muted-foreground">{Number(d.fpd_count)} defaults</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ========== SECTION 3: HOW CONTACT REDUCES FPD ========== */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-1 bg-green-500 rounded-full" />
            <h2 className="text-lg font-bold">How Early Contact Reduces FPD</h2>
          </div>

          {/* Monthly FPD Trend with Welcome Agent annotation */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">Monthly FPD Rate Trend</CardTitle>
              <CardDescription>
                FPD rates show a clear downward trajectory since the Welcome Agent program launched in September 2025
                {latestFPDRate !== null && `, dropping from a pre-program average of ${baselineFPDRate.toFixed(1)}% to ${latestFPDRate.toFixed(1)}% in Feb 2026`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={trendData}>
                  <defs>
                    <linearGradient id="fpdGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#666" />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#666" tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#666" />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <ReferenceLine
                    x={trendData.findIndex(d => d.period === "With Welcome Agent") > 0
                      ? trendData[trendData.findIndex(d => d.period === "With Welcome Agent")].month
                      : undefined}
                    yAxisId="left"
                    stroke={COLORS.success}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{ value: "Welcome Agent Launch", position: "top", fill: COLORS.success, fontSize: 11 }}
                  />
                  <ReferenceLine
                    yAxisId="left"
                    y={baselineFPDRate}
                    stroke={COLORS.muted}
                    strokeDasharray="3 3"
                    label={{ value: `Pre-program avg: ${baselineFPDRate.toFixed(1)}%`, position: "right", fill: COLORS.muted, fontSize: 10 }}
                  />
                  <Bar yAxisId="right" dataKey="total_accounts" name="Total Accounts" fill={COLORS.primary} opacity={0.2} radius={[4, 4, 0, 0]} />
                  <Area yAxisId="left" type="monotone" dataKey="fpd_rate" name="FPD Rate" stroke={COLORS.danger} strokeWidth={3} fill="url(#fpdGradient)" dot={{ fill: COLORS.danger, r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Impact of Engagement Actions */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                <CardTitle className="text-base">Impact of Borrower Engagement on FPD</CardTitle>
              </div>
              <CardDescription>
                Each engagement action measurably reduces default risk. Accounts that receive SMS, enroll in autopay,
                or add a bank account show significantly lower FPD rates compared to the baseline.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex items-center gap-3 py-2 border-b border-border/50 mb-2">
                  <div className="w-36 text-xs font-semibold text-muted-foreground uppercase">Action</div>
                  <div className="flex-1 text-xs font-semibold text-muted-foreground uppercase">FPD Rate</div>
                  <div className="w-24 text-right text-xs font-semibold text-muted-foreground uppercase">vs Baseline</div>
                </div>

                {/* Baseline */}
                <div className="flex items-center gap-3 py-2 bg-muted/10 rounded-lg px-2 mb-2">
                  <div className="w-36 text-sm font-medium text-muted-foreground">Baseline (No Action)</div>
                  <div className="flex-1">
                    <div className="h-8 bg-muted/20 rounded-full relative overflow-hidden">
                      <div
                        className="h-full rounded-full flex items-center justify-end pr-3"
                        style={{ width: `${Math.min(noSmsRate / 20 * 100, 100)}%`, backgroundColor: COLORS.muted }}
                      >
                        <span className="text-xs font-bold text-white">{formatPct(noSmsRate)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-right">
                    <span className="text-xs text-muted-foreground">—</span>
                  </div>
                </div>

                {/* SMS Impact */}
                {smsImpact?.find((r: any) => r.status === "SMS Sent") && (
                  <ImpactBar
                    label="SMS Welcome"
                    rate={Number(smsImpact.find((r: any) => r.status === "SMS Sent")!.fpd_rate)}
                    baseline={noSmsRate}
                    color={COLORS.success}
                  />
                )}

                {/* Autopay Impact */}
                {autopayImpact?.find((r: any) => r.status === "Has Autopay") && (
                  <ImpactBar
                    label="Autopay Enrolled"
                    rate={Number(autopayImpact.find((r: any) => r.status === "Has Autopay")!.fpd_rate)}
                    baseline={noAutopayRate}
                    color={COLORS.primary}
                  />
                )}

                {/* Bank Account Impact */}
                {bankImpact?.find((r: any) => r.status === "Bank Added") && (
                  <ImpactBar
                    label="Bank Account Added"
                    rate={Number(bankImpact.find((r: any) => r.status === "Bank Added")!.fpd_rate)}
                    baseline={noBankRate}
                    color={COLORS.purple}
                  />
                )}
              </div>

              <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Key Insight</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Every engagement touchpoint reduces FPD risk. The Welcome Agent program drives autopay enrollment,
                      bank account setup, and borrower awareness — all of which are measurable FPD reduction levers.
                      Combined with SMS outreach, these actions create a multi-layered defense against first payment defaults.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ========== SECTION 4: WHAT WE'RE DOING ========== */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-1 bg-blue-500 rounded-full" />
            <h2 className="text-lg font-bold">Active FPD Reduction Efforts</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPICard
              title="Consumers Contacted"
              value={accountsContacted.toLocaleString()}
              subtitle="Unique borrowers reached via calls"
              icon={Phone}
              color={COLORS.primary}
            />
            <KPICard
              title="SMS Messages Sent"
              value={smsSent.toLocaleString()}
              subtitle="Welcome messages delivered"
              icon={MessageSquare}
              color={COLORS.success}
            />
            <KPICard
              title="Autopay Enrollments"
              value={autopayEnrolled.toLocaleString()}
              subtitle="Through agent conversations"
              icon={CreditCard}
              color={COLORS.purple}
            />
            <KPICard
              title="Bank Accounts Linked"
              value={bankLinked.toLocaleString()}
              subtitle="Payment method secured"
              icon={Building}
              color={COLORS.cyan}
            />
          </div>

          {/* Program Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Program Strategy & Next Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="h-4 w-4 text-blue-400" />
                    <p className="text-sm font-semibold">Outbound Calls</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Proactive welcome calls within days of loan origination. Voicemail drops with callback number
                    for unreachable borrowers. Multi-attempt strategy for maximum contact rate.
                  </p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-green-400" />
                    <p className="text-sm font-semibold">SMS Engagement</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Welcome SMS sent to all new borrowers with callback number. Drives inbound calls and
                    establishes communication channel. Dedicated DigniFi number for brand consistency.
                  </p>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-4 w-4 text-purple-400" />
                    <p className="text-sm font-semibold">Payment Setup</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Agent guides borrowers through autopay enrollment and bank account setup during calls.
                    Each enrollment directly reduces FPD risk based on historical data.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </DashboardLayout>
  );
}
